import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Button,
    TextField,
    Box,
    Typography,
    Chip,
    IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import axiosInstance from '../../axios/axios';
import { useEncryptService } from '../../shared/hooks';
import { ColDef, GridApi } from "ag-grid-community";
import { GridOptions } from "ag-grid-community";
import AgGridTable from '../../components/table/AgGridTable';

interface Role {
    id: string;
    name: string;
    status: boolean;
    default_role: boolean;
}

interface UserData {
    id: string;
    position: number;
    firstname: string;
    lastname: string;
    organization_email: string;
    phone_number: string;
    country_code: string;
    is_active: boolean;
    invited: boolean;
    invite_status: string;
    role_id: string;
    role_name: string;
    created_at: string;
    updated_at: string;
    is_external: boolean;
}

interface APIResponse {
    status: number;
    data: {
        users_list: UserData[];
    };
}

interface RolesAPIResponse {
    status: number;
    data: Role[];
}

interface UserSelectionProps {
    onClose: () => void;
    onDone: (selectedUsers: UserData[]) => void;
    previouslySelectedUsers: UserData[];
}

const UserSelection: React.FC<UserSelectionProps> = ({
    onClose,
    onDone,
    previouslySelectedUsers,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { doEncrypt } = useEncryptService();
    const [users, setUsers] = useState<UserData[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<UserData[]>([]);
    const [internalUsers, setInternalUsers] = useState<UserData[]>([]);
    const [userInput, setUserInput] = useState('');
    const gridRef = useRef<any>(null);
    
    // Track initialization state
    const hasInitialized = useRef(false);

    // Mock external users for demonstration
    const mockExternalUsers: UserData[] = [
        { id: 'ext1', firstname: 'george', lastname: 'robert', organization_email: 'george.robert@abc.com', is_external: true, role_id: '1', role_name: 'External', position: 1, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
        { id: 'ext2', firstname: 'John', lastname: 'Doe', organization_email: 'john.doe@abc.com', is_external: true, role_id: '1', role_name: 'External', position: 2, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
        { id: 'ext3', firstname: 'Danny', lastname: 'west', organization_email: 'danny.west@abc.com', is_external: true, role_id: '1', role_name: 'External', position: 3, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
        { id: 'ext4', firstname: 'Maggy', lastname: 'Day', organization_email: 'maggy.day@abc.com', is_external: true, role_id: '1', role_name: 'External', position: 4, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
        { id: 'ext5', firstname: 'John', lastname: 'Smith', organization_email: 'john.smith@abc.com', is_external: true, role_id: '1', role_name: 'External', position: 5, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
        { id: 'ext6', firstname: 'george', lastname: 'robert', organization_email: 'george.robert@abc.com', is_external: true, role_id: '1', role_name: 'External', position: 6, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
        { id: 'ext7', firstname: 'John', lastname: 'Doe', organization_email: 'john.doe@abc.com', is_external: true, role_id: '1', role_name: 'External', position: 7, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
        { id: 'ext8', firstname: 'External', lastname: 'User8', organization_email: 'external8@abc.com', is_external: true, role_id: '1', role_name: 'External', position: 8, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
        { id: 'ext9', firstname: 'External', lastname: 'User9', organization_email: 'external9@abc.com', is_external: true, role_id: '1', role_name: 'External', position: 9, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
    ];

    useEffect(() => {
        fetchUsers();
        fetchRoles();
        // Initialize with some selected external users
        setSelectedUsers(mockExternalUsers.slice(0, 9));
    }, []);

    // Separate users into internal only when users change
    useEffect(() => {
        const internal = users.filter(user => !user.is_external);
        setInternalUsers(internal);
    }, [users]);

    // Initialize selection after data is loaded
    useEffect(() => {
        if (internalUsers.length > 0 && !hasInitialized.current) {
            hasInitialized.current = true;
            
            // Select first 2 internal users to match the image
            const initialInternalSelection = internalUsers.slice(0, 2);
            
            // Update selected users to include both external and initial internal users
            setSelectedUsers(prev => {
                const externalUsers = prev.filter(user => user.is_external);
                return [...externalUsers, ...initialInternalSelection];
            });

            // Set initial grid selection after a delay to ensure grid is ready
            setTimeout(() => {
                if (gridRef.current && gridRef.current.api) {
                    const gridApi = gridRef.current.api;
                    
                    // Clear any existing selection first
                    gridApi.deselectAll();
                    
                    // Select the initial users
                    initialInternalSelection.forEach(user => {
                        const rowNode = gridApi.getRowNode(user.id);
                        if (rowNode) {
                            rowNode.setSelected(true, false); // false prevents event from firing
                        }
                    });
                }
            }, 500);
        }
    }, [internalUsers]);

    const fetchUsers = async () => {
        try {
            const payload = { type: 1 };
            const jsonData = JSON.stringify(payload);
            const encryptedData = doEncrypt(jsonData);

            const response = await axiosInstance.post<APIResponse>(
                'authentication/org-users',
                { payload: encryptedData }
            );

            if (response.data.status === 200) {
                setUsers(response.data.data.users_list);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            // Add mock internal users for demonstration
            const mockInternalUsers: UserData[] = [
                { id: 'int1', firstname: 'steve', lastname: 'johnson', organization_email: 'steve.johnsonr26419....', is_external: false, role_id: '1', role_name: 'Role', position: 1, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
                { id: 'int2', firstname: 'steve', lastname: 'johnson', organization_email: 'steve.johnson@abc.com', is_external: false, role_id: '1', role_name: 'Role', position: 2, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
                { id: 'int3', firstname: 'steve', lastname: 'johnson', organization_email: 'steve.johnson@abc.com', is_external: false, role_id: '1', role_name: 'Role', position: 3, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
                { id: 'int4', firstname: 'steve', lastname: 'johnson', organization_email: 'steve.johnson@abc.com', is_external: false, role_id: '1', role_name: 'Admin', position: 4, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
                { id: 'int5', firstname: 'steve', lastname: 'johnson', organization_email: 'steve.johnson@abc.com', is_external: false, role_id: '1', role_name: 'Reviewer', position: 5, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
                { id: 'int6', firstname: 'steve', lastname: 'johnson', organization_email: 'steve.johnson@abc.com', is_external: false, role_id: '1', role_name: 'Reviewer', position: 6, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
                { id: 'int7', firstname: 'steve', lastname: 'johnson', organization_email: 'steve.johnson@abc.com', is_external: false, role_id: '1', role_name: 'Reviewer', position: 7, phone_number: '', country_code: '', is_active: true, invited: false, invite_status: '', created_at: '', updated_at: '' },
            ];
            setUsers(mockInternalUsers);
        }
    };

    const fetchRoles = async () => {
        try {
            const payload = { type: 1 };
            const jsonData = JSON.stringify(payload);
            const encryptedData = doEncrypt(jsonData);

            const response = await axiosInstance.post<RolesAPIResponse>(
                'authentication/org-roles',
                { payload: encryptedData }
            );

            if (response.data.status === 200) {
                setRoles(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            // Add mock roles for demonstration
            const mockRoles: Role[] = [
                { id: '1', name: 'Role', status: true, default_role: false },
                { id: '2', name: 'Admin', status: true, default_role: false },
                { id: '3', name: 'Reviewer', status: true, default_role: false },
            ];
            setRoles(mockRoles);
        }
    };

    const handleRemoveExternalUser = (userId: string) => {
        setSelectedUsers(prev => prev.filter(u => u.id !== userId));
    };

    const handleAddExternalUser = () => {
        if (userInput.trim()) {
            // Create a new external user object
            const newExternalUser: UserData = {
                id: `ext_${Date.now()}`, // Generate unique ID
                position: selectedUsers.length + 1,
                firstname: userInput.trim(),
                lastname: '',
                organization_email: `${userInput.trim().toLowerCase().replace(/\s+/g, '.')}@external.com`,
                phone_number: '',
                country_code: '',
                is_active: true,
                invited: false,
                invite_status: '',
                role_id: '1',
                role_name: 'External',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                is_external: true
            };

            // Add to selected users
            setSelectedUsers(prev => [...prev, newExternalUser]);
            
            // Clear input field
            setUserInput('');
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleAddExternalUser();
        }
    };

    const handleDone = () => {
        onDone(selectedUsers);
        onClose();
    };

    const getRoleName = (roleId: string) => {
        const role = roles.find(r => r.id === roleId);
        return role ? role.name : 'Role';
    };

    // This is the key fix - handle selection change from AgGridTable
    const handleSelectionChange = useCallback((selectedRows: UserData[]) => {
        console.log('Selection changed:', selectedRows.length);
        
        // Update selected users by keeping external users and updating internal users
        setSelectedUsers(prev => {
            const externalUsers = prev.filter(user => user.is_external);
            const newSelection = [...externalUsers, ...selectedRows];
            console.log('New total selection:', newSelection.length);
            return newSelection;
        });
    }, []);

    const columnDefs: ColDef[] = [
        {
            field: "selection",
            width: 60,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            pinned: "left",
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            lockPosition: true,
            lockPinned: true,
            cellClass: "checkbox-cell",
            headerClass: "checkbox-header",
            showDisabledCheckboxes: true,
            suppressSizeToFit: false,
        },
        {
            field: "firstname",
            width: 180,
            sortable: false,
            filter: false,
            resizable: false,
            suppressSizeToFit: true,
            valueGetter: (params) => {
                return `${params.data.firstname} ${params.data.lastname}`;
            }
        },
        {
            field: "organization_email",
            width: 250,
            sortable: false,
            filter: false,
            resizable: false,
            suppressSizeToFit: true,
        },
        {
            field: "role_id",
            width: 100,
            sortable: false,
            filter: false,
            resizable: false,
            suppressSizeToFit: true,
            valueGetter: (params) => {
                return getRoleName(params.data.role_id);
            }
        },
    ];

    const defaultColDef = {
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: false,
    };

    // Simplified grid options that work with AgGridTable
    const gridOptions: GridOptions = {
        defaultColDef,
        rowHeight: 48,
        headerHeight: 48,
        suppressCellFocus: true,
        rowStyle: { cursor: "pointer" },
        suppressRowClickSelection: true,
        rowMultiSelectWithClick: false,
        enableCellTextSelection: true,
        ensureDomOrder: true,
        rowSelection: "multiple",
        getRowId: (params) => params.data.id,
        suppressHorizontalScroll: true,
        suppressColumnVirtualisation: true,
        suppressRowVirtualisation: false,
        suppressRowDeselection: false,
        suppressMultiRangeSelection: false,
    };

    const selectedExternalUsers = selectedUsers.filter(user => user.is_external);
    const selectedInternalUsers = selectedUsers.filter(user => !user.is_external);

    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: isDarkMode ? '#ffffff' : '#000000',
                    }}
                >
                    Select Users
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ padding: '16px' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '14px',
                        color: isDarkMode ? '#cccccc' : '#666666',
                        marginBottom: '20px',
                    }}
                >
                    Only the user who have access to the reports are visible here.
                </Typography>

                {/* Users Input Field */}
                <Box sx={{ marginBottom: '20px' }}>
                    <TextField
                        label="Users"
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        fullWidth
                        variant="outlined"
                        placeholder="Type a name and press Enter to add"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: '#E2E2F2',
                                },
                            },
                        }}
                    />
                </Box>

                {/* External Users Section */}
                {selectedExternalUsers.length > 0 && (
                    <Box sx={{ marginBottom: '20px' }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: isDarkMode ? '#ffffff' : '#000000',
                                marginBottom: '8px',
                            }}
                        >
                            External Users ({selectedExternalUsers.length.toString().padStart(2, '0')})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedExternalUsers.slice(0, 7).map((user) => (
                                <Chip
                                    key={user.id}
                                    label={`${user.firstname} ${user.lastname}`}
                                    onDelete={() => handleRemoveExternalUser(user.id)}
                                    sx={{
                                        backgroundColor: isDarkMode ? '#333333' : '#f5f5f5',
                                        color: isDarkMode ? '#ffffff' : '#000000',
                                        '& .MuiChip-deleteIcon': {
                                            color: isDarkMode ? '#cccccc' : '#666666',
                                        },
                                    }}
                                />
                            ))}
                            {selectedExternalUsers.length > 7 && (
                                <Chip
                                    label={`${selectedExternalUsers.length - 7}+`}
                                    sx={{
                                        backgroundColor: isDarkMode ? '#333333' : '#f5f5f5',
                                        color: isDarkMode ? '#ffffff' : '#000000',
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                )}

                {/* Internal Users Section */}
                <Box>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: isDarkMode ? '#ffffff' : '#000000',
                            marginBottom: '8px',
                        }}
                    >
                        {selectedInternalUsers.length > 0 
                            ? `Internal Users (${selectedInternalUsers.length.toString().padStart(2, '0')})`
                            : 'Internal Users'
                        }
                    </Typography>

                    <Box
                        sx={{
                            height: '400px',
                            width: '100%',
                            border: '1px solid #E2E2F2',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            '& .ag-header': {
                                display: 'none !important',
                            },
                            '& .ag-header-row': {
                                display: 'none !important',
                            },
                            '& .ag-header-container': {
                                display: 'none !important',
                            },
                            '& .ag-paging-panel': {
                                justifyContent: 'center !important',
                                padding: '8px 16px !important',
                            },
                            '& .ag-paging-row-summary-panel': {
                                order: 2,
                            },
                            '& .ag-paging-page-summary-panel': {
                                order: 1,
                            },
                            '& .ag-paging-button': {
                                margin: '0 2px !important',
                            }
                        }}
                    >
                        <AgGridTable
                            columnDefs={columnDefs}
                            showAppsIcon={false}
                            data={internalUsers}
                            height="395px"
                            showSearch={false}
                            gridOptions={gridOptions}
                            onSelectionChange={handleSelectionChange}
                            isRowSelectable={true} // This is crucial - enables selection
                            checkboxSelection={true}
                            headerCheckboxSelection={true}
                            gridRef={gridRef} // Pass the ref to AgGridTable
                        />
                    </Box>
                </Box>
            </Box>

            {/* Footer with Done Button */}
            <Box
                sx={{
                    padding: '20px 24px',
                    borderTop: '1px solid #E2E2F2',
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    onClick={handleDone}
                    variant="contained"
                    sx={{
                        backgroundColor: '#9C27B0',
                        color: 'white',
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: '600',
                        padding: '8px 24px',
                        '&:hover': {
                            backgroundColor: '#7B1FA2',
                        },
                    }}
                >
                    Done
                </Button>
            </Box>
        </Box>
    );
};

export default UserSelection;